import React, { useState, useEffect, useMemo } from 'react';
import chatbotScript from '../data/chatbotScript.json';
import { useSupportDesk } from './redux/hooks/useSupportDesk';
import { useUserData } from './redux/hooks/useUserData';

const SupportDeskPage = () => {
  const background_image = "https://images.pexels.com/photos/4252892/pexels-photo-4252892.jpeg";  
  const {
    conversationHistory, currentScriptSection, currentStepIndex,
    searchTerm, isInitialized, collectedInfo, setConversationHistory,
    addToConversationHistory, setCurrentScriptSection,
    setCurrentStepIndex, setSearchTerm, setCollectedInfo, initializeDefaultChat,
  } = useSupportDesk();

  // Get patient data from Redux
  const { patientInfo, caseInfo } = useUserData();
  const [userInput, setUserInput] = useState('');
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [backgroundImageLoaded, setBackgroundImageLoaded] = useState(false);

  // Initialize conversation with intro messages or use persisted state
  useEffect(() => {
    if (!isInitialized && chatbotScript.intro && chatbotScript.intro.length > 1) {
      initializeDefaultChat({
        conversationHistory: [chatbotScript.intro[0], chatbotScript.intro[1]],
        currentStepIndex: 1,
      });
    }
  }, [isInitialized, initializeDefaultChat]);

  // Lazy load background image
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBackgroundImageLoaded(true);
    img.src = background_image;
  }, []);

  // Single escalation function that ALWAYS checks Redux state first
  const handleEscalation = (message = "Perfect! Let me connect you with our support team.") => {
    console.log('Escalation triggered - checking collectedInfo:', collectedInfo);
    console.log('Patient data from Redux:', patientInfo);
    
    addToConversationHistory({ message, type: "Text" });
    setCurrentScriptSection('general_support');
    
    // Check patient data from Redux first, then fallback to collected info
    const patientEmail = patientInfo?.email || collectedInfo.email;
    const patientPhone = patientInfo?.primaryNumber || collectedInfo.phoneNumber;
    const patientName = patientInfo?.firstName ? `${patientInfo.firstName} ${patientInfo.lastName}`.trim() : '';
    
    // Determine Novu subscriber ID (patient ID or email)
    const novuSubscriberId = patientInfo?.id || patientEmail;
    
    console.log('Available contact info:', { patientEmail, patientPhone, patientName, novuSubscriberId });
    
    // If we have both email and phone from either source, skip to ticket creation
    if (patientEmail && patientPhone) {
      console.log('Have all contact info, creating support ticket');
      
      // Pre-populate collected info with patient data if not already set
      if (!collectedInfo.email && patientEmail) {
        setCollectedInfo({ email: patientEmail });
      }
      if (!collectedInfo.phoneNumber && patientPhone) {
        setCollectedInfo({ phoneNumber: patientPhone });
      }
      
      const contactMessage = patientName
        ? `Perfect! I have your contact information, ${patientName}. A member of our support team will reach out to you shortly at ${patientEmail} or ${patientPhone}.`
        : `Perfect! I already have your contact information. A member of our support team will reach out to you shortly at ${patientEmail} or ${patientPhone}.`;
      
      addToConversationHistory({
        message: contactMessage,
        type: "Text"
      });
      
      // Create support ticket with patient context
      setTimeout(() => {
        createSupportTicket({
          email: patientEmail,
          phone: patientPhone,
          name: patientName,
          caseId: caseInfo?.coreServerCaseID || caseInfo?.miraSolCaseID,
          novuSubscriberId,
          conversationHistory
        });
        addToConversationHistory(chatbotScript.general_support[chatbotScript.general_support.length - 1]);
      }, 1000);
      setCurrentStepIndex(chatbotScript.general_support.length - 1);
    } else if (patientEmail || collectedInfo.email) {
      console.log('Have email, asking for phone');
      // Pre-populate email if from patient data
      if (!collectedInfo.email && patientEmail) {
        setCollectedInfo({ email: patientEmail });
      }
      setCurrentStepIndex(1);
      addToConversationHistory(chatbotScript.general_support[1]);
    } else {
      console.log('No contact info available, starting with email');
      setCurrentStepIndex(0);
      addToConversationHistory(chatbotScript.general_support[0]);
    }
  };

  // Function to create support ticket via API
  const createSupportTicket = async (ticketData) => {
    try {
      console.log('Creating support ticket with data:', ticketData);
      // TODO: Implement API call to backend
      // await api.post('/support/tickets', ticketData);
    } catch (error) {
      console.error('Error creating support ticket:', error);
    }
  };

  const handleUserResponse = (response) => {
    // Add user's response to history
    addToConversationHistory({ message: response, type: 'user_response' });

    const currentStep = chatbotScript[currentScriptSection] && chatbotScript[currentScriptSection][currentStepIndex];

    if (!currentStep) {
      // End of conversation - escalate with smart logic
      handleEscalation("I'm not sure how to proceed from here. Let me connect you with general support.");
      return;
    }

    if (currentStep.type === 'Question') {
      if (currentStep.id === 'intro_2_issue_nature') {
        // Handle main issue question
        switch (response) {
          case "Check-in help": {
            setCurrentScriptSection('help_topics');
            const checkinHelp = chatbotScript.help_topics.YearOfBirthVerification[0];
            addToConversationHistory(checkinHelp);
            setCurrentStepIndex(0);
            break;
          }
          case "Camera doesn't work":
          case "Mic doesn't work": {
            setCurrentScriptSection('help_topics');
            const deviceHelp = chatbotScript.help_topics.DeviceCheck[0];
            addToConversationHistory(deviceHelp);
            setCurrentStepIndex(0);
            break;
          }
          case "Filling forms": {
            setCurrentScriptSection('help_topics');
            const formsHelp = chatbotScript.help_topics.IntakeForms[0];
            addToConversationHistory(formsHelp);
            setCurrentStepIndex(0);
            break;
          }
          case "Insurance": {
            setCurrentScriptSection('help_topics');
            const insuranceHelp = chatbotScript.help_topics.InsuranceVerification[0];
            addToConversationHistory(insuranceHelp);
            setCurrentStepIndex(0);
            break;
          }
          default: {
            // Any other response escalates to general support
            handleEscalation("I see. Let me connect you to general support.");
            break;
          }
        }
      } else {
        // Check if this is an escalation response
        if (response === "Chat with support" || response === "Receive a call" || response === "Yes, connect me") {
          handleEscalation();
        } else if (response === "I'll try again" || response === "I'm good" || response === "No, I'm good") {
          addToConversationHistory({ message: "Great! Feel free to reach out if you need more help. You can always start a new conversation by clicking on any help topic above.", type: "Text" });
        } else {
          // For other questions, advance to next step
          const nextStepIndex = currentStepIndex + 1;
          if (chatbotScript[currentScriptSection][nextStepIndex]) {
            addToConversationHistory(chatbotScript[currentScriptSection][nextStepIndex]);
            setCurrentStepIndex(nextStepIndex);
            
            // Auto-advance Text messages
            const nextMessage = chatbotScript[currentScriptSection][nextStepIndex];
            if (nextMessage.type === 'Text' && chatbotScript[currentScriptSection][nextStepIndex + 1]) {
              setTimeout(() => {
                addToConversationHistory(chatbotScript[currentScriptSection][nextStepIndex + 1]);
                setCurrentStepIndex(nextStepIndex + 1);
              }, 1500);
            }
          } else {
            // End of section - escalate with smart logic
            handleEscalation("I'm not sure how to proceed from here. Let me connect you with general support.");
          }
        }
      }
    } else {
      // If it's not a question, advance to next step
      const nextStepIndex = currentStepIndex + 1;
      if (chatbotScript[currentScriptSection][nextStepIndex]) {
        addToConversationHistory(chatbotScript[currentScriptSection][nextStepIndex]);
        setCurrentStepIndex(nextStepIndex);
        
        // Auto-advance Text messages
        const nextMessage = chatbotScript[currentScriptSection][nextStepIndex];
        if (nextMessage.type === 'Text' && chatbotScript[currentScriptSection][nextStepIndex + 1]) {
          setTimeout(() => {
            addToConversationHistory(chatbotScript[currentScriptSection][nextStepIndex + 1]);
            setCurrentStepIndex(nextStepIndex + 1);
          }, 1500);
        }
      } else {
        // End of section - escalate with smart logic
        if (currentScriptSection === 'general_support') {
          setTimeout(() => {
            addToConversationHistory(chatbotScript.general_support[chatbotScript.general_support.length - 1]);
          }, 1000);
        } else {
          handleEscalation("I'm not sure how to proceed from here. Let me connect you with general support.");
        }
      }
    }
  };

  const handleInputChange = (e) => {
    setUserInput(e.target.value);
  };

  const handleSubmitInput = (e) => {
    e.preventDefault();
    if (userInput.trim()) {
      // Store collected information based on current step
      const lastMessage = conversationHistory[conversationHistory.length - 1];
      
      if (currentScriptSection === 'general_support') {
        if (lastMessage.type === 'Email') {
          console.log('Storing email:', userInput.trim());
          setCollectedInfo({ email: userInput.trim() });
        } else if (lastMessage.type === 'Free Input') {
          if (lastMessage.id === 'general_call_prompt') {
            console.log('Storing phone number:', userInput.trim());
            setCollectedInfo({ phoneNumber: userInput.trim() });
          } else if (lastMessage.id === 'general_backup_call_prompt') {
            console.log('Storing backup phone number:', userInput.trim());
            setCollectedInfo({ backupPhoneNumber: userInput.trim() });
          }
        }
      }
      
      handleUserResponse(userInput);
      setUserInput('');
    }
  };

  // Render current message and input/options
  const renderChatbotContent = () => {
    const lastMessage = conversationHistory[conversationHistory.length - 1];
    if (!lastMessage) return null;

    return (
      <div className="chatbot-conversation">
        {conversationHistory.map((msg, index) => (
          <div key={index} className={`chat-message ${msg.type === 'user_response' ? 'user' : 'bot'}`}>
            {msg.message}
          </div>
        ))}
        {lastMessage.type === 'Question' && lastMessage.answers && (
          <div className="chat-options">
            {lastMessage.answers.map((answer, index) => (
              <button key={index} onClick={() => handleUserResponse(answer)} className="chat-option-button">
                {answer}
              </button>
            ))}
          </div>
        )}
        {(lastMessage.type === 'Free Input' || lastMessage.type === 'Free Input (Multi-Line)' || lastMessage.type === 'Email') && (
          <form onSubmit={handleSubmitInput} className="chat-input-form">
            <input
              type={lastMessage.type === 'Email' ? 'email' : 'text'}
              value={userInput}
              onChange={handleInputChange}
              placeholder={lastMessage.type === 'Email' ? 'Enter your email address...' : 'Type your response...'}
              className="chat-input-field"
            />
            <button type="submit" className="chat-send-button">Send</button>
          </form>
        )}
        {lastMessage.type === 'Create Ticket' && (
          <div className="chat-ticket-created text-center">
            <p>✅ Support ticket has been created successfully!</p>
            <button
              onClick={() => {
                setConversationHistory([chatbotScript.intro[0], chatbotScript.intro[1]]);
                setCurrentScriptSection('intro');
                setCurrentStepIndex(1);
              }}
              className="chat-restart-button"
            >
              Start New Conversation
            </button>
          </div>
        )}
      </div>
    );
  };

  // Generate dynamic featured articles from help_topics (memoized to prevent infinite loops)
  const allFeaturedArticles = useMemo(() => {
    return Object.keys(chatbotScript.help_topics).map(topicKey => {
      const firstHelpMessage = chatbotScript.help_topics[topicKey][0];
      const messages = chatbotScript.help_topics[topicKey].map(msg => msg.message).join(' ');
      return {
        title: `Help with ${topicKey.replace(/([A-Z])/g, ' $1').trim()}`,
        link: `#`,
        messageId: firstHelpMessage.id,
        topicKey: topicKey,
        messages: messages
      };
    });
  }, []);

  useEffect(() => {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    const filtered = allFeaturedArticles.filter(article => {
      const titleMatches = article.title.toLowerCase().includes(lowerCaseSearchTerm);
      const contentMatches = article.messages.toLowerCase().includes(lowerCaseSearchTerm);
      return titleMatches || contentMatches;
    });

    setDisplayedArticles(filtered);
  }, [searchTerm, allFeaturedArticles]);

  const handleSearch = (event) => {
    event.preventDefault();
  };

  return (
    <div className="support-desk-container" style={{height:'90vh'}}>
      {/* Header Section */}
      <div className="support-desk-header" style={{
        backgroundImage: backgroundImageLoaded ? `url("${background_image}")` : 'none',
        backgroundColor: '#4a90e2', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
        padding: '60px 20px', borderRadius: '8px', marginBottom: '20px'
      }}>
        <h2 style={{color:'#404040'}}>How can we help?</h2>
        <form onSubmit={handleSearch} className="search-bar-container">
          <input
            type="text"
            name="search"
            placeholder="Type your question"
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button type="submit" className="search-button">
            <i className="fas fa-search"></i>
          </button>
        </form>
      </div>

      <div className='row'>
        <div className='col-sm-6'>
          {/* Tabs/Categories */}
          <div className="support-desk-tabs">
            <button className="tab-button active">Check-in Help</button>
            <button className="tab-button">General Support</button>
          </div>
          
          {/* Featured Articles Section */}
          <div className="featured-articles-section">
            <h3>Featured articles</h3>
            <div className="articles-grid">
              {displayedArticles.length > 0 ? (
                displayedArticles.map((article, index) => (
                  <div key={index} className="article-item">
                    <a href={article.link} onClick={(e) => {
                      e.preventDefault();
                      // Add the selected topic to the existing conversation instead of resetting
                      const firstMessageForTopic = chatbotScript.help_topics[article.topicKey][0];
                      addToConversationHistory(firstMessageForTopic);
                      setCurrentScriptSection('help_topics');
                      setCurrentStepIndex(0);
                    }}>
                      {article.title}
                    </a>
                  </div>
                ))
              ) : (
                <p>No articles found matching your search.</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Chatbot Integration */}
        <div className="col-sm-6 chatbot-section">
          {renderChatbotContent()}
        </div>
      </div>
    </div>
  );
};

export default SupportDeskPage;